import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BookOpen, Play, ClipboardCheck, Download, Search, ExternalLink, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useOnboarding } from "@/context/OnboardingContext";
import OnboardingChecklist from "@/components/OnboardingChecklist";

// Compact in-app version of the workflow testing guide.
const SECTIONS = [
  { id: 1, title: "Executive Dashboard", route: "/", routes: ["/"],
    what: [
      "AI Briefing narrative at the top",
      "Today's Operational Summary — 6 KPI cards",
      "CQC Readiness score with breakdown bars",
      "Medication Safety card",
    ],
    how: [
      "Open the dashboard root URL",
      "Confirm every widget populates within ~3 seconds",
      "Click each operational summary card to jump to its module",
      "Download the CQC board report PDF",
    ],
  },
  { id: 2, title: "Medication (eMAR + MAR + CD Ledger)", route: "/mar-chart",
    routes: ["/mar-chart", "/cd-ledger", "/delivery-notes"],
    what: [
      "Record dose: Administered, Refused, Withheld or PRN",
      "Controlled Drugs require a witness signature",
      "Missed doses auto-push after 30-min grace",
      "PRN guard blocks repeat within minimum interval",
    ],
    how: [
      "Open /mar-chart and pick any service user",
      "Click a blank cell for today, choose Administered, type signature, save",
      "Try to save a CD without a witness — should reject",
      "Try to record a PRN twice within an hour — should block",
    ],
  },
  { id: 3, title: "Rota & Staff Schedule", route: "/rota",
    routes: ["/rota", "/staff-schedule", "/my-shifts"],
    what: [
      "Assign carer to visit, publish, notify",
      "Weekly templates + generator",
      "Drag & drop in Staff Schedule",
      "AI Rota Optimiser",
    ],
    how: [
      "Open /rota, assign a visit, click Publish",
      "Log in as carer or /my-shifts and Accept",
      "Open /staff-schedule and drag a shift onto another staff row",
      "Click AI Optimise for AI-suggested reassignments",
    ],
  },
  { id: 4, title: "Service Users", route: "/clients",
    routes: ["/clients"],
    what: [
      "12 profile sections including Care Plan, Medication, Vitals, Photos",
      "Multi-photo gallery",
      "Vitals with NEWS2 flags",
      "Care plan templates + AI generation",
    ],
    how: [
      "Open any client profile",
      "Add a vitals reading with systolic 190 → red critical badge",
      "Upload two photos to the gallery",
      "Generate a care plan from template",
    ],
  },
  { id: 5, title: "Staff & Users", route: "/users",
    routes: ["/users", "/staff"],
    what: [
      "Add Staff Wizard (Microsoft 365-style)",
      "Filter by Role / Site / Status",
      "Right-to-Work document tracking",
      "Profile photo crop tool",
    ],
    how: [
      "Open /users → Add Staff Member → complete wizard",
      "Upload a profile photo and crop",
      "Upload an RTW document with expiry",
      "Filter by Role → Manager",
    ],
  },
  { id: 6, title: "Care Plans", route: "/care-plans",
    routes: ["/care-plans", "/risk-register"],
    what: [
      "Overdue / due-soon / current status",
      "AI Care Plan generation",
      "Review dates auto-tracked",
    ],
    how: [
      "Open /care-plans and find an overdue plan",
      "Open the plan, set next review to today+30",
      "Click Generate with AI",
    ],
  },
  { id: 7, title: "Family Portal", route: "/family-portal",
    routes: ["/family-portal", "/family-inbox"],
    what: [
      "Tokenised read-only portal",
      "Two-way messaging (family ↔ staff)",
      "Weekly Care Digest PDF via Resend",
    ],
    how: [
      "Open /family-portal, pick a client, Create link",
      "Open /family/{token} in incognito",
      "Send message from family view → check /family-inbox",
    ],
  },
  { id: 8, title: "Audit Centre & Board Reports", route: "/audit",
    routes: ["/audit"],
    what: [
      "Every change logged with old/new values",
      "Filter by user / entity / date",
      "CQC Evidence Pack PDF",
    ],
    how: [
      "Edit a training record",
      "Open /audit — the change appears with old and new values",
      "Click Export CQC Evidence Pack",
    ],
  },
  { id: 9, title: "Push Notifications", route: "/",
    routes: ["/"],
    what: [
      "Missed dose, critical vitals, appointment reminders",
      "Rota publish nudge",
      "Push history log",
    ],
    how: [
      "Top of dashboard: Enable desktop alerts",
      "Grant browser permission",
      "Trigger a missed dose or critical vitals",
    ],
  },
];

const DOCX_URL = "/downloads/Ayobell_Care_Workflow_Testing_Guide.docx";

export default function HelpCentre() {
  const { open, setOpen, setTourActive, mark } = useOnboarding();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("guide");

  useEffect(() => { if (!open) setQ(""); }, [open]);

  const filtered = SECTIONS.filter((s) => {
    if (!q) return true;
    const hay = `${s.title} ${s.what.join(" ")} ${s.how.join(" ")}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="help-centre-modal">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 font-heading text-lg">
            <BookOpen className="w-4 h-4 text-sky-700" /> Help Centre
          </DialogTitle>
          <DialogDescription className="text-[13px] text-slate-500">
            Walk through every workflow, run the guided tour, or check your onboarding progress.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
          <div className="px-6 pt-3">
            <TabsList data-testid="help-tabs">
              <TabsTrigger value="guide" data-testid="help-tab-guide"><BookOpen className="w-3.5 h-3.5 mr-1" /> Workflow guide</TabsTrigger>
              <TabsTrigger value="tour" data-testid="help-tab-tour"><Play className="w-3.5 h-3.5 mr-1" /> Guided tour</TabsTrigger>
              <TabsTrigger value="checklist" data-testid="help-tab-checklist"><ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Onboarding</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="guide" className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3 data-[state=inactive]:hidden">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  data-testid="help-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search modules and steps…"
                  className="pl-8 h-9"
                />
              </div>
              <a href={DOCX_URL} download data-testid="help-download-docx">
                <Button variant="outline" size="sm" className="h-9">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download .docx
                </Button>
              </a>
            </div>

            {filtered.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">No sections match &quot;{q}&quot;.</div>
            )}
            {filtered.map((s) => (
              <details
                key={s.id}
                className="border border-slate-200 rounded-lg group open:shadow-sm"
                data-testid={`help-section-${s.id}`}
                open={q.length > 0}
              >
                <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2 hover:bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-800 text-[11px] font-semibold flex items-center justify-center shrink-0">{s.id}</span>
                    <span className="font-semibold text-slate-900 truncate">{s.title}</span>
                  </div>
                  <Link
                    to={s.route}
                    onClick={() => setOpen(false)}
                    className="text-[11px] uppercase tracking-widest text-sky-700 hover:text-sky-900 flex items-center gap-1 shrink-0"
                    data-testid={`help-section-open-${s.id}`}
                  >
                    Open <ExternalLink className="w-3 h-3" />
                  </Link>
                </summary>
                <div className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">What to check</div>
                    <ul className="text-sm text-slate-700 space-y-1 list-disc pl-4">
                      {s.what.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">How to test</div>
                    <ol className="text-sm text-slate-700 space-y-1 list-decimal pl-4">
                      {s.how.map((t, i) => <li key={i}>{t}</li>)}
                    </ol>
                  </div>
                </div>
              </details>
            ))}
          </TabsContent>

          <TabsContent value="tour" className="flex-1 min-h-0 overflow-y-auto px-6 py-6 data-[state=inactive]:hidden">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-sky-100 flex items-center justify-center mb-3">
                <Play className="w-6 h-6 text-sky-700" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900">Take the guided tour</h3>
              <p className="mt-1.5 text-[14px] text-slate-600">
                A 10-step walk-through of every core area of Ayobell Care. We&apos;ll navigate for you, highlight the right widgets and tick off your onboarding checklist as we go.
              </p>
              <Button
                className="mt-5 bg-sky-700 hover:bg-sky-800 h-11 px-5"
                data-testid="help-start-tour"
                onClick={() => { setOpen(false); setTourActive(true); mark("visit_dashboard"); }}
              >
                Start the tour <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <div className="mt-3 text-[11px] uppercase tracking-widest text-slate-400">
                Takes about 2 minutes · you can exit any time
              </div>
            </div>
          </TabsContent>

          <TabsContent value="checklist" className="flex-1 min-h-0 overflow-y-auto px-6 py-4 data-[state=inactive]:hidden">
            <OnboardingChecklist onNavigate={() => setOpen(false)} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
